// FloRiver — presentational FLO v3.0 river visualization (NumaTool hero).
// See scratchpad/FLO_RIVER_SPEC.md, "Component 1", for the contract.
//
// Static riverbed = one <Svg> background (water gradient, 7 day columns for
// the `window` zone, INTAKE pool on the left, LATER strip on the right,
// sediment floor at the bottom). Orbs = absolutely-positioned Animated.View
// + Pressable overlays above it — react-native-svg@15.12.1 only, no
// reanimated/Skia; continuous motion uses RN's built-in `Animated` API with
// `useNativeDriver: true`. No data fetching / Supabase here — this file only
// renders a `RiverModel` (see `../lib/flo-weights`) that the caller builds.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  Animated,
  Easing,
  LayoutChangeEvent,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, { Circle, Defs, G, Line, LinearGradient, Path, RadialGradient, Rect, Stop, Text as SvgText } from 'react-native-svg';
import { useTheme, makeThemedStyles, DISPLAY_FONT, type V10Palette } from '../v10/theme';
import { SECTORS, Sector } from '../domain/olmps';
import type { RiverItem, RiverModel } from '../lib/flo-weights';
import { RiverWater } from './RiverWater';

export interface FloRiverProps {
  model: RiverModel;
  view: 'weight' | 'sector';
  onPressItem: (item: RiverItem) => void;
  height?: number;
  /** The caption + colour key under the river. On in the full NUMA tool; off
   *  on the dashboard band, where it is just noise under the widget. */
  showLegend?: boolean;
}

const DEFAULT_HEIGHT = 600;
const MAX_VISIBLE_ORBS = 60;
const ORB_MIN = 26;
const ORB_MAX = 60;
const ORB_WEIGHT_SCALE = 3.2;
const MAX_WEIGHT_FOR_DEPTH = 12.5; // 10 (normalized-priority ceiling) x 1.25 (overdue modifier)

/** Orb colours come from the palette: scheduled = gold, unprocessed/paused =
 *  ivory (the "ball"), overdue = amber sinking to red, stones = deep gold. */
function stateFill(t: V10Palette): Record<'scheduled' | 'unprocessed' | 'paused', string> {
  return { scheduled: t.gold, unprocessed: t.ivory, paused: t.ivory };
}

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ============================================================================
// Geometry
// ============================================================================

interface RiverGeometry {
  width: number;
  height: number;
  intakeWidth: number;
  windowWidth: number;
  laterWidth: number;
  dayColumnWidth: number;
  floorHeight: number;
  floorTop: number;
  waterTop: number;
  waterBottom: number;
}

function buildGeometry(width: number, height: number): RiverGeometry {
  const floorHeight = Math.max(36, Math.min(52, height * 0.13));
  const intakeWidth = width * 0.15;
  const laterWidth = width * 0.15;
  const windowWidth = Math.max(1, width - intakeWidth - laterWidth);
  const dayColumnWidth = windowWidth / 7;
  const floorTop = height - floorHeight;
  const waterTop = 20;
  const waterBottom = Math.max(waterTop + 10, floorTop - 12);
  return { width, height, intakeWidth, windowWidth, laterWidth, dayColumnWidth, floorHeight, floorTop, waterTop, waterBottom };
}

// ── The river's banks. The water meanders left→right between two curved
// banks instead of filling the whole panel; everything that swims is kept
// inside them, and the land outside is darker.
const MEANDER_WAVES = 1.15; // ≈ two to three gentle bends across the panel
function bankAt(x: number, geo: RiverGeometry): { top: number; bottom: number; mid: number } {
  const span = geo.waterBottom - geo.waterTop;
  const amp = Math.min(span * 0.025, geo.width * 0.02);
  const halfW = span * 0.5 * 0.92;
  const mid = (geo.waterTop + geo.waterBottom) / 2 + amp * Math.sin((x / geo.width) * Math.PI * 2 * MEANDER_WAVES + 0.6);
  // The mouth (LATER) widens, the spring (INTAKE) narrows a touch.
  const h = halfW;
  return { top: mid - h, bottom: mid + h, mid };
}

/** Closed path of the water body (top bank left→right, bottom bank back). */
function riverBodyPath(geo: RiverGeometry, inset = 0): string {
  const n = 48;
  const top: string[] = [];
  const bottom: string[] = [];
  for (let i = 0; i <= n; i++) {
    const x = (geo.width * i) / n;
    const b = bankAt(x, geo);
    top.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${(b.top + inset).toFixed(1)}`);
    bottom.push(`L${x.toFixed(1)},${(b.bottom - inset).toFixed(1)}`);
  }
  return `${top.join(' ')} ${bottom.reverse().join(' ')} Z`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Small deterministic string hash → [0, 1), so jitter/bob timing is stable
 *  across re-renders for a given item id (no per-render randomness). */
function hashUnit(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

const TITLE_MAX_CHARS = 28;

function truncateTitle(title: string): string {
  return title.length > TITLE_MAX_CHARS ? `${title.slice(0, TITLE_MAX_CHARS).trimEnd()}...` : title;
}

function formatDayLabel(dayIndex: number, now: Date): string {
  if (dayIndex === 0) return 'Today';
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + dayIndex);
  return `${DAY_ABBR[d.getDay()]} ${d.getDate()}`;
}

function columnKeyFor(item: RiverItem): string {
  if (item.zone === 'intake') return 'intake';
  if (item.zone === 'later') return 'later';
  return `day-${item.dayIndex ?? 0}`;
}

function xFor(item: RiverItem, geo: RiverGeometry, jitterX: number): number {
  if (item.zone === 'intake') {
    return geo.intakeWidth / 2 + jitterX * geo.intakeWidth * 0.3;
  }
  if (item.zone === 'later') {
    return geo.intakeWidth + geo.windowWidth + geo.laterWidth / 2 + jitterX * geo.laterWidth * 0.3;
  }
  const dayIdx = item.dayIndex ?? 0;
  const colCenter = geo.intakeWidth + dayIdx * geo.dayColumnWidth + geo.dayColumnWidth / 2;
  return colCenter + jitterX * geo.dayColumnWidth * 0.3;
}

/** Width of the column an item lives in (orbs are capped to fit it). */
function columnWidthFor(item: RiverItem, geo: RiverGeometry): number {
  if (item.zone === 'intake') return geo.intakeWidth;
  if (item.zone === 'later') return geo.laterWidth;
  return geo.dayColumnWidth;
}

function orbDiameter(item: RiverItem, geo: RiverGeometry): number {
  const raw = clamp(ORB_MIN + item.weight * ORB_WEIGHT_SCALE, ORB_MIN, ORB_MAX);
  return Math.max(14, Math.min(raw, columnWidthFor(item, geo) * 0.85));
}

/** Vertical room for the settled (non-overdue) lane in a column. */
function settledLaneHeight(geo: RiverGeometry): number {
  // The water is ~0.92 of the panel's height between its banks.
  return (geo.waterBottom - geo.waterTop) * 0.92 * 0.7;
}

/** How many orbs fit a column at >= 0.6-diameter spacing (min 4). */
function columnCapacity(avgDiameter: number, geo: RiverGeometry): number {
  return Math.max(4, Math.floor(settledLaneHeight(geo) / (0.6 * avgDiameter)));
}

const MORE_PREFIX = 'more:';

/**
 * Lays out every non-stone item at once (rather than per-item in isolation)
 * so items sharing a column get distinct, non-overlapping vertical lanes.
 * Only items in 'overdue' state are allowed into the lower band near the
 * floor (scaled by their own sinkProgress) — everything else stays settled
 * in the upper band, however small its weight/priority is.
 */
function assignPositions(
  items: RiverItem[],
  geo: RiverGeometry,
  view: 'weight' | 'sector',
  moreColumns: Set<string> = new Set()
): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();

  const groups = new Map<string, RiverItem[]>();
  for (const item of items) {
    const key = columnKeyFor(item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  }

  for (const [groupKey, group] of groups.entries()) {
    const ordered = group.slice().sort((a, b) => b.weight - a.weight || a.id.localeCompare(b.id));
    // This column's stretch of water: between the banks at its centre.
    const bank = bankAt(xFor(ordered[0], geo, 0), geo);
    const bandTop = bank.top + 6;
    const bandBottom = bank.bottom - 6;
    const bandHeight = bandBottom - bandTop;
    const settledBandBottom = bandTop + bandHeight * 0.7; // lower 30% reserved for overdue sinking
    const sinkingBandTop = settledBandBottom;
    const sinkingBandBottom = bandBottom;

    let laneItems: RiverItem[];
    if (view === 'sector') {
      // Sector view groups by sector lane instead — handled per-item below.
      laneItems = [];
    } else {
      laneItems = ordered.filter((i) => i.state !== 'overdue');
    }
    const sinkingItems = view === 'weight' ? ordered.filter((i) => i.state === 'overdue') : [];

    const laneCount = laneItems.length + (moreColumns.has(groupKey) ? 1 : 0);
    const laneH = laneCount > 0 ? (settledBandBottom - bandTop) / laneCount : 0;
    laneItems.forEach((item, i) => {
      // Seeded jitter, alternating sides so neighbours in a lane never stack.
      const side = i % 2 === 0 ? -1 : 1;
      const jitterX = side * (0.35 + 0.65 * hashUnit(`${item.id}:x`));
      const jitterY = hashUnit(`${item.id}:y`) * 2 - 1;
      const x = xFor(item, geo, jitterX);
      const y = bandTop + (i + 0.5) * laneH + jitterY * Math.min(4, laneH * 0.1);
      positions.set(item.id, { x: clamp(x, 16, geo.width - 16), y: clamp(y, 12, geo.floorTop - 6) });
    });
    if (moreColumns.has(groupKey)) {
      const y = bandTop + (laneItems.length + 0.5) * laneH;
      const probe = laneItems[0] ?? ordered[0];
      positions.set(`${MORE_PREFIX}${groupKey}`, {
        x: clamp(xFor(probe, geo, 0), 16, geo.width - 16),
        y: clamp(y, 12, geo.floorTop - 6),
      });
    }

    sinkingItems.forEach((item, i) => {
      const jitterX = hashUnit(`${item.id}:x`) * 2 - 1;
      const laneH = (sinkingBandBottom - sinkingBandTop) / sinkingItems.length;
      const laneCenter = sinkingBandTop + (i + 0.5) * laneH;
      const x = xFor(item, geo, jitterX);
      const y = sinkingBandTop + item.sinkProgress * (laneCenter - sinkingBandTop + laneH / 2);
      positions.set(item.id, { x: clamp(x, 16, geo.width - 16), y: clamp(y, 12, geo.floorTop - 6) });
    });

    if (view === 'sector') {
      const lanes: (Sector | null)[] = [...SECTORS, null];
      ordered.forEach((item) => {
        const jitterX = hashUnit(`${item.id}:x`) * 2 - 1;
        const x = xFor(item, geo, jitterX);
        const laneIdx = Math.max(0, lanes.indexOf(item.sector));
        const laneH = bandHeight / lanes.length;
        let y = bandTop + laneIdx * laneH + laneH / 2;
        if (item.state === 'overdue') {
          y = Math.min(bandTop + laneIdx * laneH + laneH - 6, y + item.sinkProgress * laneH * 0.3);
        }
        positions.set(item.id, { x: clamp(x, 16, geo.width - 16), y: clamp(y, 12, geo.floorTop - 6) });
      });
    }
  }

  return positions;
}

function stonePositionFor(item: RiverItem, geo: RiverGeometry): { x: number; y: number } {
  const jitterX = hashUnit(`${item.id}:x`) * 2 - 1;
  const jitterY = hashUnit(`${item.id}:y`) * 2 - 1;
  const dayIdx = item.dayIndex ?? 0;
  const colCenter = geo.intakeWidth + dayIdx * geo.dayColumnWidth + geo.dayColumnWidth / 2;
  const x = colCenter + jitterX * geo.dayColumnWidth * 0.3;
  const y = geo.floorTop + geo.floorHeight * 0.45 + jitterY * geo.floorHeight * 0.18;
  return { x: clamp(x, 16, geo.width - 16), y: clamp(y, 12, geo.floorTop - 6) };
}

// ============================================================================
// Color
// ============================================================================

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const num = parseInt(clean, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function lerpColor(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}

/** Lighten (amt>0, toward white) or darken (amt<0, toward black) a #hex or
 *  rgb() colour. Unknown formats pass through unchanged. */
function shade(color: string, amt: number): string {
  let rgb: [number, number, number] | null = null;
  if (/^#[0-9a-f]{6}$/i.test(color)) rgb = hexToRgb(color);
  else {
    const m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(color);
    if (m) rgb = [Number(m[1]), Number(m[2]), Number(m[3])];
  }
  if (!rgb) return color;
  const target = amt >= 0 ? 255 : 0;
  const k = Math.abs(amt);
  const [r, g, b] = rgb.map((c) => Math.round(c + (target - c) * k));
  return `rgb(${r}, ${g}, ${b})`;
}

// Orb colour = how long it has sat + how late it is. New work is black; it
// darkens through indigo and plum the longer it stays open; overdue turns it
// ember → blood red as it sinks. All stay dark so the light water reads.
const AGE_RAMP: [number, string][] = [
  [0, '#0b0d12'], // new: black
  [3, '#121a35'], // a few days: midnight blue
  [7, '#22184a'], // a week: indigo
  [14, '#3a1648'], // two weeks: plum
  [30, '#4d1233'], // a month+: wine
];
export const ORB_OVERDUE_FROM = '#5e2410';
export const ORB_OVERDUE_TO = '#7d0f12';
function rampAt(stops: [number, string][], v: number): string {
  if (v <= stops[0][0]) return stops[0][1];
  for (let i = 1; i < stops.length; i++) {
    const [d1, c1] = stops[i];
    const [d0, c0] = stops[i - 1];
    if (v <= d1) return lerpColor(c0, c1, (v - d0) / (d1 - d0));
  }
  return stops[stops.length - 1][1];
}
export function orbAgeColor(days: number): string {
  return rampAt(AGE_RAMP, days);
}
function colorForItem(item: RiverItem, _t: V10Palette): { fill: string; opacity: number } {
  if (item.isStone) return { fill: '#3a0b0e', opacity: 1 };
  if (item.state === 'overdue') return { fill: lerpColor(ORB_OVERDUE_FROM, ORB_OVERDUE_TO, item.sinkProgress), opacity: 1 };
  const fill = orbAgeColor(item.ageDays ?? 0);
  if (item.state === 'paused') return { fill, opacity: 0.55 };
  return { fill, opacity: 1 };
}

// ============================================================================
// Reduced motion (built into RN — no new dependency)
// ============================================================================

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled()
      .then((value) => {
        if (mounted) setReduced(!!value);
      })
      .catch(() => {});
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', (value: boolean) => setReduced(!!value));
    return () => {
      mounted = false;
      sub.remove();
    };
  }, []);
  return reduced;
}

// ============================================================================
// Orb
// ============================================================================

/** A lit sphere: highlight top-left, darker rim, optional label. */
function OrbSphere({
  uid,
  diameter,
  fill,
  label,
  labelColor,
  labelStyle,
}: {
  uid: string;
  diameter: number;
  fill: string;
  label: string;
  labelColor: string;
  labelStyle: object;
}) {
  const gid = `orb-${uid.replace(/[^a-zA-Z0-9]/g, '_')}`;
  const r = diameter / 2;
  const showLabel = r >= 9;
  return (
    <View style={{ width: diameter, height: diameter }}>
      <Svg width={diameter} height={diameter} style={StyleSheet.absoluteFill}>
        <Defs>
          <RadialGradient id={gid} cx="0.36" cy="0.3" rx="0.78" ry="0.78" fx="0.32" fy="0.26" gradientUnits="objectBoundingBox">
            <Stop offset="0" stopColor={shade(fill, 0.7)} />
            <Stop offset="0.4" stopColor={fill} />
            <Stop offset="1" stopColor={shade(fill, -0.5)} />
          </RadialGradient>
        </Defs>
        <Circle cx={r} cy={r} r={r - 0.75} fill={`url(#${gid})`} stroke={shade(fill, -0.6)} strokeOpacity={0.55} strokeWidth={1} />
        <Circle cx={r * 0.72} cy={r * 0.62} r={r * 0.2} fill="#ffffff" fillOpacity={0.35} />
      </Svg>
      {showLabel ? (
        <View style={[StyleSheet.absoluteFill, { alignItems: 'center', justifyContent: 'center' }]} pointerEvents="none">
          <Text style={[labelStyle, { color: labelColor, fontSize: r >= 14 ? 9 : 8 }]} numberOfLines={1}>
            {label}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function MoreOrb({ id, x, y, diameter, count, fill, labelColor }: { id: string; x: number; y: number; diameter: number; count: number; fill: string; labelColor: string }) {
  const styles = useStyles();
  return (
    <View pointerEvents="none" style={{ position: 'absolute', left: x - diameter / 2, top: y - diameter / 2, alignItems: 'center' }}>
      <OrbSphere
        uid={id}
        diameter={diameter}
        fill={fill}
        label={`+${count}`}
        labelColor={labelColor}
        labelStyle={styles.orbWeight}
      />
      <Text style={styles.moreOrbCaption} numberOfLines={1}>
        more
      </Text>
    </View>
  );
}

function TaskOrb({
  item,
  x,
  y,
  diameter,
  fill,
  fillOpacity,
  labelColor,
  showTitle,
  bobDurationMs,
  bobDelayMs,
  reducedMotion,
  onPress,
}: {
  item: RiverItem;
  x: number;
  y: number;
  diameter: number;
  fill: string;
  fillOpacity: number;
  labelColor: string;
  showTitle: boolean;
  bobDurationMs: number;
  bobDelayMs: number;
  reducedMotion: boolean;
  onPress: (item: RiverItem) => void;
}) {
  const styles = useStyles();
  const bob = useRef(new Animated.Value(0)).current;
  const glow = useRef(new Animated.Value(0.35)).current;
  const pulsing = item.state === 'overdue' && !item.isStone;

  useEffect(() => {
    if (item.isStone || reducedMotion) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(bob, {
          toValue: 1,
          duration: bobDurationMs,
          delay: bobDelayMs,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(bob, {
          toValue: 0,
          duration: bobDurationMs,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [item.isStone, reducedMotion, bobDurationMs, bobDelayMs, bob]);

  useEffect(() => {
    if (!pulsing || reducedMotion) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(glow, { toValue: 0.9, duration: 900, easing: Easing.inOut(Easing.quad), useNativeDriver: true }),
        Animated.timing(glow, { toValue: 0.35, duration: 900, easing: Easing.inOut(Easing.quad), useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [pulsing, reducedMotion, glow]);

  const translateY = bob.interpolate({ inputRange: [0, 1], outputRange: [-3, 3] });
  const haloDiameter = diameter + (item.isStone ? 4 : 18);
  const titleWidth = Math.max(diameter + 12, 72);

  return (
    <View
      pointerEvents="box-none"
      style={{
        position: 'absolute',
        left: x - haloDiameter / 2,
        top: y - haloDiameter / 2,
        width: haloDiameter,
        alignItems: 'center',
      }}
    >
      {pulsing ? (
        <Animated.View
          pointerEvents="none"
          style={[
            styles.glowHalo,
            {
              position: 'absolute',
              left: 0,
              top: 0,
              width: haloDiameter,
              height: haloDiameter,
              borderRadius: haloDiameter / 2,
              opacity: glow,
            },
          ]}
        />
      ) : null}
      <Animated.View
        style={{
          width: haloDiameter,
          height: haloDiameter,
          alignItems: 'center',
          justifyContent: 'center',
          transform: item.isStone ? undefined : [{ translateY }],
        }}
      >
        <Pressable hitSlop={6} onPress={() => onPress(item)}>
          <View style={{ opacity: fillOpacity }}>
            <OrbSphere
              uid={item.id}
              diameter={diameter}
              fill={fill}
              label={item.weight.toFixed(1)}
              labelColor={labelColor}
              labelStyle={styles.orbWeight}
            />
          </View>
        </Pressable>
      </Animated.View>
      {showTitle ? (
        <Text style={[styles.orbTitle, { width: titleWidth }]} numberOfLines={2}>
          {truncateTitle(item.title)}
        </Text>
      ) : null}
      {item.isStone ? (
        <Text style={styles.stoneLabel} numberOfLines={1}>
          {item.daysOverdue}d late
        </Text>
      ) : null}
      {item.isStone && item.lastNote ? (
        <Text style={[styles.stoneLabel, { width: titleWidth, opacity: 0.85 }]} numberOfLines={2}>
          {item.lastNote}
        </Text>
      ) : null}
    </View>
  );
}

// ============================================================================
// FloRiver
// ============================================================================

export function FloRiver({
  model,
  view,
  onPressItem,
  height = DEFAULT_HEIGHT,
  showLegend = true,
}: FloRiverProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const [panelWidth, setPanelWidth] = useState(320);
  const reducedMotion = useReducedMotion();
  const now = useRef(new Date()).current;

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const w = e.nativeEvent.layout.width;
      if (w > 0 && Math.abs(w - panelWidth) > 1) setPanelWidth(w);
    },
    [panelWidth]
  );

  const geometry = useMemo(() => buildGeometry(panelWidth, height), [panelWidth, height]);

  const { visibleItems, hiddenCount } = useMemo(() => {
    if (model.items.length <= MAX_VISIBLE_ORBS) {
      return { visibleItems: model.items, hiddenCount: 0 };
    }
    const byWeightDesc = model.items.slice().sort((a, b) => b.weight - a.weight);
    return { visibleItems: byWeightDesc.slice(0, MAX_VISIBLE_ORBS), hiddenCount: model.items.length - MAX_VISIBLE_ORBS };
  }, [model.items]);

  // Per-column capacity: when a column holds more settled orbs than fit at
  // >= 0.6-diameter spacing, show the heaviest N-1 and a "+K more" orb.
  const { shownItems, moreByColumn } = useMemo(() => {
    const more = new Map<string, number>();
    if (view !== 'weight') return { shownItems: visibleItems, moreByColumn: more };
    const cols = new Map<string, RiverItem[]>();
    for (const item of visibleItems) {
      if (item.isStone || item.state === 'overdue') continue;
      const key = columnKeyFor(item);
      if (!cols.has(key)) cols.set(key, []);
      cols.get(key)!.push(item);
    }
    const hidden = new Set<string>();
    for (const [key, list] of cols.entries()) {
      const avg = list.reduce((sum, i) => sum + orbDiameter(i, geometry), 0) / list.length;
      const cap = columnCapacity(avg, geometry);
      if (list.length <= cap) continue;
      const ordered = list.slice().sort((a, b) => b.weight - a.weight || a.id.localeCompare(b.id));
      for (const item of ordered.slice(cap - 1)) hidden.add(item.id);
      more.set(key, list.length - (cap - 1));
    }
    return { shownItems: visibleItems.filter((i) => !hidden.has(i.id)), moreByColumn: more };
  }, [visibleItems, geometry, view]);

  const positions = useMemo(() => {
    const swimming = shownItems.filter((i) => !i.isStone);
    const laidOut = assignPositions(swimming, geometry, view, new Set(moreByColumn.keys()));
    for (const item of shownItems) {
      if (item.isStone) laidOut.set(item.id, stonePositionFor(item, geometry));
    }
    return laidOut;
  }, [shownItems, moreByColumn, geometry, view]);

  return (
    <View>
    <View style={[styles.panel, { height }]} onLayout={onLayout}>
      {/* The water itself: an animated shader, flowing INTAKE → LATER. */}
      <RiverWater
        width={geometry.width}
        height={height}
        waterTop={geometry.waterTop}
        waterBottom={geometry.waterBottom}
        intakeWidth={geometry.intakeWidth}
        laterWidth={geometry.laterWidth}
        animate={!reducedMotion}
      />
      <Svg width={geometry.width} height={height} style={StyleSheet.absoluteFill}>
        <Defs>
          <LinearGradient id="floRiverFloor" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor={t.navyRaised} />
            <Stop offset="1" stopColor={t.navy} />
          </LinearGradient>
        </Defs>

        {/* Day-label row backing */}
        <Rect x={0} y={0} width={geometry.width} height={22} fill={t.glass} />

        {/* Bank hairline over the shader's shoreline */}
        <Path d={riverBodyPath(geometry)} fill="none" stroke={t.goldDeep} strokeWidth={1} strokeOpacity={0.45} />

        {/* Today column highlight */}
        <Rect
          x={geometry.intakeWidth}
          y={0}
          width={geometry.dayColumnWidth}
          height={geometry.floorTop}
          fill={t.goldGlow}
          fillOpacity={0.18}
        />

        {/* Day column boundaries (dashed) */}
        {Array.from({ length: 8 }).map((_, i) => (
          <Line
            key={`col-${i}`}
            x1={geometry.intakeWidth + i * geometry.dayColumnWidth}
            y1={4}
            x2={geometry.intakeWidth + i * geometry.dayColumnWidth}
            y2={geometry.floorTop}
            stroke="#06223a"
            strokeOpacity={0.55}
            strokeWidth={1.4}
            strokeDasharray="5,5"
          />
        ))}

        {/* Day labels */}
        {Array.from({ length: 7 }).map((_, i) => (
          <SvgText
            key={`day-${i}`}
            x={geometry.intakeWidth + i * geometry.dayColumnWidth + geometry.dayColumnWidth / 2}
            y={14}
            fontSize={9}
            fontWeight={i === 0 ? 'bold' : 'normal'}
            fill={i === 0 ? t.goldBright : t.ivorySoft}
            textAnchor="middle"
          >
            {formatDayLabel(i, now)}
          </SvgText>
        ))}

        {/* Intake pool */}
        <Line
          x1={geometry.intakeWidth}
          y1={0}
          x2={geometry.intakeWidth}
          y2={geometry.floorTop}
          stroke="#06223a"
          strokeOpacity={0.7}
          strokeWidth={1.6}
        />
        <G rotation={-90} origin={[geometry.intakeWidth / 2, geometry.floorTop / 2]}>
          <SvgText
            x={geometry.intakeWidth / 2}
            y={geometry.floorTop / 2}
            fontSize={8}
            fontWeight="bold"
            fill={t.ivoryFaint}
            textAnchor="middle"
            letterSpacing={2}
          >
            INTAKE
          </SvgText>
        </G>

        {/* Later strip */}
        <Line
          x1={geometry.intakeWidth + geometry.windowWidth}
          y1={0}
          x2={geometry.intakeWidth + geometry.windowWidth}
          y2={geometry.floorTop}
          stroke="#06223a"
          strokeOpacity={0.7}
          strokeWidth={1.6}
        />
        <G
          rotation={-90}
          origin={[geometry.intakeWidth + geometry.windowWidth + geometry.laterWidth / 2, geometry.floorTop / 2]}
        >
          <SvgText
            x={geometry.intakeWidth + geometry.windowWidth + geometry.laterWidth / 2}
            y={geometry.floorTop / 2}
            fontSize={8}
            fontWeight="bold"
            fill={t.ivoryFaint}
            textAnchor="middle"
            letterSpacing={2}
          >
            LATER
          </SvgText>
        </G>

        {/* Sector lane labels (sector view only) */}
        {view === 'sector'
          ? [...SECTORS, null].map((sector, i) => {
              const lanes = SECTORS.length + 1;
              const laneH = (geometry.waterBottom - geometry.waterTop) / lanes;
              const yc = geometry.waterTop + i * laneH + laneH / 2;
              return (
                <SvgText
                  key={sector ?? '—'}
                  x={6}
                  y={yc + 3}
                  fontSize={9}
                  fontWeight="bold"
                  fill={t.ivorySoft}
                >
                  {sector ?? '—'}
                </SvgText>
              );
            })
          : null}

        {/* Floor band */}
        <Rect x={0} y={geometry.floorTop} width={geometry.width} height={height - geometry.floorTop} fill="url(#floRiverFloor)" />
      </Svg>

      <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
        {Array.from(moreByColumn.entries()).map(([key, count]) => {
          const pos = positions.get(`${MORE_PREFIX}${key}`);
          if (!pos) return null;
          const colW = key === 'intake' ? geometry.intakeWidth : key === 'later' ? geometry.laterWidth : geometry.dayColumnWidth;
          return (
            <MoreOrb
              key={`${MORE_PREFIX}${key}`}
              id={`${MORE_PREFIX}${key}`}
              x={pos.x}
              y={pos.y}
              diameter={Math.max(24, Math.min(36, colW * 0.85))}
              count={count}
              fill={orbAgeColor(0)}
              labelColor={t.ivory}
            />
          );
        })}
        {shownItems.map((item, index) => {
          const pos = positions.get(item.id) ?? { x: geometry.width / 2, y: geometry.waterTop };
          const diameter = orbDiameter(item, geometry);
          const { fill, opacity } = colorForItem(item, t);
          const showTitle = !item.isStone && diameter >= 22;
          const durationSeed = hashUnit(`${item.id}:t`);
          return (
            <TaskOrb
              key={item.id}
              item={item}
              x={pos.x}
              y={pos.y}
              diameter={diameter}
              fill={fill}
              fillOpacity={opacity}
              labelColor={t.ivory}
              showTitle={showTitle}
              bobDurationMs={2400 + durationSeed * 1200}
              bobDelayMs={(index % 6) * 140}
              reducedMotion={reducedMotion}
              onPress={onPressItem}
            />
          );
        })}
      </View>

      {hiddenCount > 0 ? (
        <View style={styles.moreBadge} pointerEvents="none">
          <Text style={styles.moreBadgeText}>+{hiddenCount} more</Text>
        </View>
      ) : null}

      {model.items.length === 0 ? (
        <View style={styles.emptyOverlay} pointerEvents="none">
          <Text style={styles.emptyText}>The river is calm — nothing open right now.</Text>
        </View>
      ) : null}
    </View>

    {showLegend ? (
    <View style={styles.legend}>
      <Text style={styles.legendCaption}>
        Columns = due date (INTAKE = unscheduled, LATER = further out). Number = priority weight. Colour = how long it has sat open; red = overdue.
      </Text>
      <View style={styles.legendRow}>
        {[
          { label: 'New', color: orbAgeColor(0) },
          { label: '1 week', color: orbAgeColor(7) },
          { label: '2 weeks', color: orbAgeColor(14) },
          { label: 'Month+', color: orbAgeColor(30) },
          { label: 'Overdue', color: ORB_OVERDUE_TO },
        ].map((k) => (
          <View key={k.label} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: k.color, borderWidth: 1, borderColor: t.navyLine }]} />
            <Text style={styles.legendLabel}>{k.label}</Text>
          </View>
        ))}
      </View>
    </View>
    ) : null}
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
  panel: {
    width: '100%',
    borderRadius: 22,
    overflow: 'hidden',
    backgroundColor: t.night,
    borderWidth: 1,
    borderColor: t.navyLine,
    boxShadow: `0 10px 30px ${t.glowSoft}`,
  } as object,
  orb: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: t.navyLine,
  },
  orbStone: {
    borderColor: t.lineFaint,
  },
  moreOrbCaption: {
    marginTop: 1,
    fontSize: 7,
    fontWeight: '700',
    letterSpacing: 1,
    color: t.ivorySoft,
    textTransform: 'uppercase',
  },
  orbWeight: {
    fontSize: 9,
    fontWeight: '800',
    color: '#f3ead7',
  },
  orbTitle: {
    marginTop: 2,
    fontSize: 7,
    lineHeight: 8,
    fontWeight: '600',
    color: t.ivory,
    textAlign: 'center',
    fontFamily: DISPLAY_FONT,
    textTransform: 'uppercase',
    textShadowColor: 'rgba(0,0,0,0.9)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 3,
  },
  stoneLabel: {
    marginTop: 1,
    fontSize: 9,
    fontWeight: '700',
    color: t.behind,
    textAlign: 'center',
  },
  glowHalo: {
    backgroundColor: t.lapsed,
  },
  legend: {
    marginTop: 8,
    paddingHorizontal: 4,
  },
  legendCaption: {
    fontSize: 11,
    color: t.ivorySoft,
    marginBottom: 6,
  },
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  legendDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
  },
  legendLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: t.ivory,
  },
  moreBadge: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    backgroundColor: t.glassStrong,
    borderWidth: 1,
    borderColor: t.navyLine,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  moreBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: t.ivory,
  },
  emptyOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  emptyText: {
    fontSize: 12,
    fontWeight: '600',
    color: t.ivorySoft,
    textAlign: 'center',
  },
}),
);
